import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { Table, InputNumber, Empty } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// NAVIGATION
import { useLocation } from "react-router-dom";
// REDUX
import { useSelector } from "react-redux";
import { useLazyGetBankQuery } from "../../../redux/banks/bankAPI";
import { usePutLiftMutation } from "../../../redux/lifts/liftAPI";
// UTILS
import { getTableRowClass } from "../../../utils/utils";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
// FLATBUFFERS
import { formatUuidT } from "../../../flatbuffers/display";
// COMPONENTS
import BanksLiftsPositionsHeader from "./BanksLiftsPositionsHeader";
import GlobalAlert2 from "../../home/GlobalAlert2";
import TableNoDataBox from "../../components/table/TableNoDataBox";
import TableColumnLiftType from "../../components/table/TableColumnLiftType";
// INTERFACES
import { BankInterface } from "../../../interfaces/Bank.interface";
import { RootState } from "../../../app/store";
// CONSTANTS
import { BLANK_BANK } from "../../../constants/bank";

function BanksLiftsPositionsTable() {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const location = useLocation();
  const bankId = new URLSearchParams(location.search).get("idBank");
  const { Column } = Table;
  const [t] = useTranslation("global");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [bank, setBank] = useState<BankInterface>({ ...BLANK_BANK });
  const [errorMessage, setErrorMessage] = useState<any>({});

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { theme } = useSelector((state: RootState) => state.home);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [
    triggerGetBank,
    {
      data,
      isLoading: isLoadingGetBank,
      isFetching: isFetchingGetBank,
      isError: isErrorGetBank,
      error: errorGetBank,
    },
  ] = useLazyGetBankQuery();

  const [
    triggerPutLift,
    {
      isSuccess: isSuccessPutLift,
      isError: isErrorPutLift,
      error: errorPutLift,
      isLoading: isLoadingPutLift,
      reset: resetPutLift,
    },
  ] = usePutLiftMutation();

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const getInitialData = async () => {
    const token = await GETJwtToken();
    triggerGetBank({
      bankId,
      token,
    });
  };

  const onClickSave = async () => {
    const token = await GETJwtToken();
    if (bank && bank.lifts) {
      bank.lifts.forEach((lift) => {
        triggerPutLift({
          id: lift.id,
          body: lift,
          token,
        });
      });
    }
  };

  const refreshPage = () => {
    getInitialData();
  };

  const onChangePosition = (e: number, record: any) => {
    if (bank.lifts) {
      const newLifts = bank.lifts.map((lift: any) =>
        lift.id === record.id ? { ...lift, position: e } : lift,
      );
      setBank((prev) => ({ ...prev, lifts: newLifts }));
    }
  };

  const checkInputStatus = (record: any) => {
    let isValid = true;
    if (bank && bank.lifts) {
      bank.lifts.forEach((lift: any) => {
        if (
          lift &&
          record &&
          record.id &&
          record.id !== lift.id &&
          record.position &&
          lift.position &&
          record.position === lift.position
        ) {
          isValid = false;
        }
      });
    }
    return isValid;
  };

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    getInitialData();
    return () => {
      resetPutLift();
    };
  }, []);

  useEffect(() => {
    if (data && data.data) {
      setBank(data.data);
    }
  }, [data]);

  useEffect(() => {
    if (errorGetBank) {
      setErrorMessage(errorGetBank);
    }
  }, [errorGetBank]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div>
      <div className="mb-2">
        <BanksLiftsPositionsHeader
          bankName={String(bank.name ?? "")}
          onClickSave={onClickSave}
          onClickRefreshPage={refreshPage}
        />
      </div>
      <Table
        rowClassName={(record, index) => getTableRowClass(index, theme)}
        locale={{
          emptyText: (
            <>
              {isErrorGetBank ? (
                <TableNoDataBox
                  isError={isErrorGetBank}
                  errorMessage={
                    errorMessage && errorMessage.data && errorMessage.data.message
                      ? errorMessage.data.message
                      : "Error"
                  }
                  noItemsFound="Error"
                />
              ) : (
                <div className="generalStyles__flexCenter" style={{ height: "300px" }}>
                  <Empty description={t("messages.noData") || "No data available"} />
                </div>
              )}
            </>
          ),
        }}
        className="customTable"
        dataSource={bank.lifts}
        rowKey={(record: any) => formatUuidT(record.id)}
        size="small"
        loading={isLoadingGetBank || isLoadingPutLift || isFetchingGetBank}
      >
        {/** NAME */}
        <Column
          title={t("general.name")}
          dataIndex="name"
          key="name"
          render={(text) => <p>{text}</p>}
        />
        {/** TYPE */}
        <Column
          title={t("general.type")}
          dataIndex="type"
          key="type"
          render={(text, record: any) => <TableColumnLiftType liftType={record.type} />}
        />
        {/** POSITION */}
        <Column
          title={t("general.position")}
          dataIndex="position"
          key="position"
          render={(text, record) => (
            <InputNumber
              placeholder={t("edgeManagement.banks.floorName")}
              value={text}
              onChange={(e) => {
                onChangePosition(e, record);
              }}
              status={checkInputStatus(record) ? "" : "error"}
            />
          )}
        />
      </Table>
      <GlobalAlert2
        isError={isErrorPutLift}
        isSuccess={isSuccessPutLift}
        requestType="PUT"
        error={errorPutLift}
        name="Bank"
      />
    </div>
  );
}

export default BanksLiftsPositionsTable;
